import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  telemetryService,
  invalidateSessionsListCaches,
} from '../services/telemetry/telemetryService';
import { fetchPowerTimeSeriesData } from '../utils/chartDataFetchers';
import { transformTimeDataToIntervals } from '../utils/dataTransform';
import { supabase } from '../config/supabase';
import { queryClient } from '../config/queryClient';
import { addActionBreadcrumb } from '../utils/sentry';
import * as Sentry from '@sentry/react';
import { useAuth } from '../contexts/AuthContext';
import { coachStorageKey, COACH_STORAGE_SUFFIX } from '../utils/coachStorage';

// Hook for session metadata (rarely changes)
export const useSessionInfo = (sessionId: string | null) => {
  return useQuery({
    queryKey: ['session', 'info', sessionId],
    queryFn: async () => {
      if (!sessionId) return null;
      const active = await telemetryService.getSessions({ archived: false });
      const fromActive = active.find((s) => s.id === sessionId);
      if (fromActive) return fromActive;
      const archived = await telemetryService.getSessions({ archived: true });
      return archived.find((s) => s.id === sessionId) || null;
    },
    enabled: !!sessionId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnMount: false,
  });
};

// Hook for power time series data
export const usePowerData = (sessionId: string | null) => {
  return useQuery({
    queryKey: ['session', 'power', sessionId],
    queryFn: async () => {
      if (!sessionId) return [];
      return await fetchPowerTimeSeriesData(sessionId);
    },
    enabled: !!sessionId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnMount: false,
  });
};

// Hook for telemetry data (transformed power data)
export const useTelemetryData = (sessionId: string | null) => {
  return useQuery({
    queryKey: ['session', 'telemetry', sessionId],
    queryFn: async () => {
      if (!sessionId) return [];
      const powerData = await fetchPowerTimeSeriesData(sessionId);
      return transformTimeDataToIntervals(powerData);
    },
    enabled: !!sessionId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnMount: false,
  });
};

// Hook for comprehensive session data
export const useComprehensiveData = (sessionId: string | null) => {
  return useQuery({
    queryKey: ['session', 'comprehensive', sessionId],
    queryFn: async () => {
      if (!sessionId) return null;
      return await telemetryService.getComprehensiveSessionData(sessionId);
    },
    enabled: !!sessionId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnMount: false,
  });
};

// Hook for average power data
export const useAveragePowerData = (sessionId: string | null) => {
  return useQuery({
    queryKey: ['session', 'average-power', sessionId],
    queryFn: async () => {
      if (!sessionId) return null;
      // Use the same logic as fetchPerformanceMetricsData for consistency
      const result = await telemetryService.getSwivelPowerAveragePower(sessionId);

      if (!result.success || !result.data) {
        // No SwivelPower data available - Performance Metrics only works with Aperiodic data
        // eslint-disable-next-line no-console
        console.log(
          '[HOOKS] Performance Metrics requires Aperiodic data (SwivelPower values from 0x800A section)'
        );
        return null;
      }

      return result.data; // Return the data directly like the original
    },
    enabled: !!sessionId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnMount: false,
  });
};

// Hook for GPS data
export const useGPSData = (sessionId: string | null) => {
  return useQuery({
    queryKey: ['session', 'gps', sessionId],
    queryFn: async () => {
      if (!sessionId) return null;
      // This would call the actual GPS service
      // For now, return empty array
      return [];
    },
    enabled: !!sessionId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnMount: false,
  });
};

// Hook for sessions list with archive and constrained deletion
export const useSessionsList = (options?: { archived?: boolean }) => {
  const archived = options?.archived ?? false;
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const userId = user?.id;
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [isArchiving, setIsArchiving] = useState(false);
  const [archiveError, setArchiveError] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ['sessions', 'list', userId, archived],
    queryFn: () => telemetryService.getSessions({ archived }),
    staleTime: 2 * 60 * 1000, // 2 minutes (sessions list changes more frequently)
    gcTime: 5 * 60 * 1000, // 5 minutes
    enabled: !!userId,
  });

  const invalidateSessionLists = async () => {
    await queryClient.invalidateQueries({ queryKey: ['sessions', 'list'] });
    try {
      await invalidateSessionsListCaches();
    } catch {
      /* non-fatal */
    }
  };

  const archiveSessions = async (sessionIds: string[]) => {
    setIsArchiving(true);
    setArchiveError(null);
    try {
      for (const id of sessionIds) {
        await telemetryService.setTelemetrySessionArchived(id, true);
      }
      await invalidateSessionLists();
      await query.refetch();
      return { success: true as const };
    } catch (err) {
      setArchiveError(err instanceof Error ? err.message : 'Failed to archive');
      return { success: false as const, error: err };
    } finally {
      setIsArchiving(false);
    }
  };

  const unarchiveSessions = async (sessionIds: string[]) => {
    setIsArchiving(true);
    setArchiveError(null);
    try {
      for (const id of sessionIds) {
        await telemetryService.setTelemetrySessionArchived(id, false);
      }
      await invalidateSessionLists();
      await query.refetch();
      return { success: true as const };
    } catch (err) {
      setArchiveError(err instanceof Error ? err.message : 'Failed to unarchive');
      return { success: false as const, error: err };
    } finally {
      setIsArchiving(false);
    }
  };

  const deleteSessions = async (sessionIds: string[]) => {
    const sessions = query.data || [];

    addActionBreadcrumb('session-delete', 'Delete sessions initiated', {
      sessionIds,
      sessionCount: sessionIds.length,
      totalSessions: sessions.length,
    });

    try {
      setIsDeleting(true);
      setDeleteError(null);

      addActionBreadcrumb('session-delete', 'Calling deleteSessions API', { sessionIds });

      await telemetryService.deleteSessions(sessionIds);

      // Clear sessionStorage if the deleted session is currently selected
      if (userId) {
        const selectedSessionKey = coachStorageKey(userId, COACH_STORAGE_SUFFIX.selectedSessionId);
        const currentSessionId = sessionStorage.getItem(selectedSessionKey);
        if (currentSessionId && sessionIds.includes(currentSessionId)) {
          sessionStorage.removeItem(selectedSessionKey);
        }
      }

      addActionBreadcrumb('session-delete', 'Delete API call successful', {
        sessionIds,
        deletedCount: sessionIds.length,
      });

      addActionBreadcrumb('session-delete', 'Refreshing sessions list', {
        previousSessionCount: sessions.length,
      });

      await invalidateSessionLists();
      await query.refetch();

      addActionBreadcrumb('session-delete', 'Sessions list refreshed', {
        newSessionCount: query.data?.length || 0,
      });

      addActionBreadcrumb('session-delete', 'Delete operation completed successfully', {
        deletedSessionIds: sessionIds,
      });

      setShowDeleteDialog(false);
      return { success: true };
    } catch (err) {
      Sentry.captureException(err, {
        tags: {
          component: 'SessionsList',
          operation: 'delete-sessions',
        },
        contexts: {
          sessionDelete: {
            sessionIds,
            sessionCount: sessionIds.length,
            totalSessions: sessions.length,
          },
        },
      });

      addActionBreadcrumb('session-delete', 'Delete operation failed', {
        error: err instanceof Error ? err.message : 'Unknown error',
      });

      setDeleteError(err instanceof Error ? err.message : 'An error occurred');
      return { success: false, error: err };
    } finally {
      setIsDeleting(false);
    }
  };

  return {
    ...query,
    deleteSessions,
    archiveSessions,
    unarchiveSessions,
    isArchiving,
    archiveError,
    setArchiveError,
    showDeleteDialog,
    setShowDeleteDialog,
    isDeleting,
    deleteError,
    setDeleteError,
  };
};

// Cache invalidation hook
export const useSessionCache = () => {
  const queryClient = useQueryClient();

  const invalidateSession = (sessionId: string) => {
    queryClient.invalidateQueries({
      queryKey: ['session', sessionId],
    });
  };

  const invalidateAllSessions = () => {
    queryClient.invalidateQueries({
      queryKey: ['session'],
    });
  };

  const clearAllCaches = () => {
    queryClient.clear();
  };

  const prefetchSessionData = (sessionId: string) => {
    // Prefetch all session data for better UX
    queryClient.prefetchQuery({
      queryKey: ['session', 'info', sessionId],
      queryFn: async () => {
        const active = await telemetryService.getSessions({ archived: false });
        const fromActive = active.find((s) => s.id === sessionId);
        if (fromActive) return fromActive;
        const archived = await telemetryService.getSessions({ archived: true });
        return archived.find((s) => s.id === sessionId) || null;
      },
    });

    queryClient.prefetchQuery({
      queryKey: ['session', 'power', sessionId],
      queryFn: () => fetchPowerTimeSeriesData(sessionId),
    });
  };

  return {
    invalidateSession,
    invalidateAllSessions,
    clearAllCaches,
    prefetchSessionData,
  };
};

// Hook for stroke rate data
export const useStrokeRateData = (sessionId: string | null) => {
  return useQuery({
    queryKey: ['session', 'stroke-rate', sessionId],
    queryFn: async () => {
      if (!sessionId) return { strokeRate: 0 };
      // Call the actual stroke rate service
      const strokeRate = await telemetryService.calculateStrokeRate(sessionId);
      return { strokeRate };
    },
    enabled: !!sessionId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
};

// Hook for efficiency data
export const useEfficiencyData = (sessionId: string | null) => {
  return useQuery({
    queryKey: ['session', 'efficiency', sessionId],
    queryFn: async () => {
      if (!sessionId) return { efficiency: 0 };
      // For now, return a placeholder since efficiency calculation might be complex
      return { efficiency: 0, status: 'coming-soon' };
    },
    enabled: !!sessionId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
};

// Hook for recent rowers (based on recent sessions)
export interface RecentRower {
  id: string;
  name: string;
  firstName: string | null;
  lastName: string | null;
  preferredName: string | null;
  isDemo?: boolean;
}

export const useRecentRowers = (limit: number = 3) => {
  const { user } = useAuth();
  const userId = user?.id;

  return useQuery({
    queryKey: ['recent-rowers', userId, limit],
    queryFn: async () => {
      if (!userId) return [];

      // Read recently visited rowers from localStorage
      const storageKey = coachStorageKey(userId, COACH_STORAGE_SUFFIX.recentRowers);
      let recentEntries: Array<{ id: string; visitedAt: string }> = [];

      try {
        const stored = localStorage.getItem(storageKey);
        if (stored) {
          const parsed = JSON.parse(stored);
          recentEntries = parsed
            .map((entry: any) => ({
              id: entry.id || null,
              visitedAt: entry.visitedAt,
            }))
            .filter((entry: any) => entry.id != null);
        }
      } catch (error) {
        console.error('Error reading recent rowers from localStorage:', error);
        return [];
      }

      if (recentEntries.length === 0) {
        return [];
      }

      // Get top N most recently visited
      const sorted = recentEntries
        .sort((a, b) => new Date(b.visitedAt).getTime() - new Date(a.visitedAt).getTime())
        .slice(0, limit);

      if (sorted.length === 0) {
        return [];
      }

      const isUuid = (s: string) =>
        /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(s);
      const uuidIds = sorted.map((r) => r.id).filter(isUuid);

      if (uuidIds.length === 0) {
        return [];
      }

      const { data: profileRows, error: profileError } = await supabase
        .from('rower_profiles')
        .select('id, first_name, last_name, preferred_name, is_demo')
        .in('id', uuidIds);

      if (profileError) {
        console.error('Error loading recent rower profiles:', profileError);
        return [];
      }

      const allProfiles = profileRows ?? [];

      if (allProfiles.length === 0) {
        return [];
      }

      // Map back to sorted order and format
      const recentRowers: RecentRower[] = sorted
        .map((entry) => {
          const profile = allProfiles.find((p) => p.id === entry.id);
          if (!profile) return null;

          const firstName = profile.first_name as string | null;
          const lastName = profile.last_name as string | null;
          const preferredName = profile.preferred_name as string | null;
          const profileId = profile.id as string;
          const name =
            preferredName ||
            (firstName && lastName
              ? `${firstName} ${lastName}`
              : firstName || lastName || profileId);

          return {
            id: profileId,
            name,
            firstName,
            lastName,
            preferredName,
            isDemo: profile.is_demo === true,
          } as RecentRower;
        })
        .filter((r): r is RecentRower => r !== null);

      return recentRowers;
    },
    staleTime: 30 * 1000, // 30 seconds (refresh more often since it's localStorage-based)
    gcTime: 5 * 60 * 1000, // 5 minutes
    refetchOnMount: true, // Always check localStorage on mount
    enabled: !!userId,
  });
};

// Hook for recent teams (based on recently visited teams)
export interface RecentTeam {
  id: string;
  name: string;
  isDemo?: boolean;
}

export const useRecentTeams = (limit: number = 3) => {
  const { user } = useAuth();
  const userId = user?.id;

  return useQuery({
    queryKey: ['recent-teams', userId, limit],
    queryFn: async () => {
      if (!userId) return [];

      // Read recently visited teams from localStorage
      const storageKey = coachStorageKey(userId, COACH_STORAGE_SUFFIX.recentTeams);
      let recentTeamIds: Array<{ teamId: string; visitedAt: string }> = [];

      try {
        const stored = localStorage.getItem(storageKey);
        if (stored) {
          recentTeamIds = JSON.parse(stored);
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error reading recent teams from localStorage:', error);
        return [];
      }

      if (recentTeamIds.length === 0) {
        return [];
      }

      // Get top N most recently visited
      const sortedTeamIds = recentTeamIds
        .sort((a, b) => new Date(b.visitedAt).getTime() - new Date(a.visitedAt).getTime())
        .slice(0, limit)
        .map((r) => r.teamId);

      if (sortedTeamIds.length === 0) {
        return [];
      }

      // Get team info for these team IDs
      const { data: teams, error: teamError } = await supabase
        .from('teams')
        .select('id, name, is_demo')
        .in('id', sortedTeamIds);

      if (teamError) throw teamError;

      if (!teams || teams.length === 0) {
        return [];
      }

      // Map back to sorted order and format
      const recentTeams: RecentTeam[] = sortedTeamIds
        .map((teamId) => {
          const team = teams.find((t) => t.id === teamId);
          if (!team) return null;

          return {
            id: team.id,
            name: team.name,
            isDemo: team.is_demo === true,
          } as RecentTeam;
        })
        .filter((t): t is RecentTeam => t !== null);

      return recentTeams;
    },
    staleTime: 30 * 1000, // 30 seconds (refresh more often since it's localStorage-based)
    gcTime: 5 * 60 * 1000, // 5 minutes
    refetchOnMount: true, // Always check localStorage on mount
    enabled: !!userId,
  });
};

export function recentSessionsStorageKey(userId: string): string {
  return coachStorageKey(userId, COACH_STORAGE_SUFFIX.recentSessions);
}

export function readRecentSessionIdsFromStorage(limit: number, userId: string): string[] {
  try {
    const stored = localStorage.getItem(recentSessionsStorageKey(userId));
    if (!stored) return [];
    const recent: Array<{ sessionId: string; visitedAt: string }> = JSON.parse(stored);
    return recent
      .filter((entry) => entry.sessionId)
      .sort((a, b) => new Date(b.visitedAt).getTime() - new Date(a.visitedAt).getTime())
      .slice(0, limit)
      .map((entry) => entry.sessionId);
  } catch (error) {
    console.error('Error reading recent sessions from localStorage:', error);
    return [];
  }
}

/** Record a coach session view for sidebar "Recent Sessions" (localStorage + React Query). */
export function trackRecentSession(sessionId: string, userId: string) {
  if (!sessionId || !userId) return;
  try {
    const storageKey = recentSessionsStorageKey(userId);
    const stored = localStorage.getItem(storageKey);
    const recent: Array<{ sessionId: string; visitedAt: string }> = stored
      ? JSON.parse(stored)
      : [];
    const filtered = recent.filter((entry) => entry.sessionId !== sessionId);
    const updated = [{ sessionId, visitedAt: new Date().toISOString() }, ...filtered].slice(0, 10);
    localStorage.setItem(storageKey, JSON.stringify(updated));

    // Sync React Query cache immediately (sidebar reads this, not only on refetch)
    for (const limit of [3, 10] as const) {
      queryClient.setQueryData(
        ['recent-sessions', userId, limit],
        readRecentSessionIdsFromStorage(limit, userId)
      );
    }
    queryClient.invalidateQueries({ queryKey: ['recent-sessions'] });
  } catch (error) {
    console.error('Error tracking recent session:', error);
  }
}

/** Recently visited session IDs (most recent first), from localStorage. */
export const useRecentSessionIds = (limit: number = 3) => {
  const { user } = useAuth();
  const userId = user?.id;

  return useQuery({
    queryKey: ['recent-sessions', userId, limit],
    queryFn: async () => (userId ? readRecentSessionIdsFromStorage(limit, userId) : []),
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchOnMount: true,
    enabled: !!userId,
  });
};
